import { Inject, Injectable } from '@angular/core';
import '@splitsoftware/splitio';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { useSubscriptionLatest } from '../core/hooks';
import { getTreatment } from '../split';
import { SPLIT_CLIENT } from '../split/split.guard';

export class Split<T extends string> {
  constructor(
    readonly name: string,
    readonly stable: T,
    readonly treatments: T[],
  ) {}
}

@Injectable()
export class TreatmentService {
  constructor(
    @Inject(SPLIT_CLIENT) private readonly splitClient: SplitIO.IClient,
  ) {}
  treatments = new Subject<{ split: Split<string>; value: string | null }>();

  private key<T extends string>(split: Split<T>): string {
    return `__SPLIT_OVERRIDE__${split.name}`;
  }

  public setOverride<T extends string>(split: Split<T>, value: T | null) {
    if (value === null) {
      localStorage.removeItem(this.key(split));
    } else {
      localStorage.setItem(this.key(split), value);
    }
    this.treatments.next({ split, value });
  }

  // TODO(matt): remove this sometime after 8/1/2021.
  private migrateLegacyKey<T extends string>(split: Split<T>) {
    const legacyKeyValue = localStorage.getItem(split.name);
    if (legacyKeyValue !== null) {
      this.setOverride(split, split.treatments[+legacyKeyValue]);
      localStorage.removeItem(split.name);
    }
  }

  public getOverride<T extends string>(split: Split<T>): T | null {
    this.migrateLegacyKey(split);
    return localStorage.getItem(this.key(split)) as T | null;
  }

  public get<T extends string>(split: Split<T>): Observable<T> {
    const override = this.getOverride(split);
    if (override !== null) {
      return this.treatments.pipe(
        filter(v => v.split === split),
        map(v => v.value as T),
        startWith(override),
      );
    } else {
      return this.getWithoutOverride(split);
    }
  }

  public getWithoutSplit<T extends string>(
    split: Split<T>,
  ): Observable<T | null> {
    return of(this.getOverride(split));
  }

  public getWithoutOverride<T extends string>(split: Split<T>): Observable<T> {
    return getTreatment<T>(this.splitClient, split.name).pipe(
      map(treatment => (treatment === 'control' ? split.stable : treatment)),
    );
  }
}

export function useSplitTreatment<T extends string>(
  treatmentService: TreatmentService,
  split: Split<T>,
): T | undefined {
  return useSubscriptionLatest(treatmentService.get(split), []);
}

export function useSplitTreatmentBoolean<T extends string>(
  treatmentService: TreatmentService,
  split: Split<T>,
  onEnum: string,
): boolean {
  const splitVal = useSplitTreatment(treatmentService, split);
  return splitVal === onEnum;
}

export function useNonSplitTreatment<T extends string>(
  treatmentService: TreatmentService,
  split: Split<T>,
): T | null | undefined {
  return useSubscriptionLatest(treatmentService.getWithoutSplit(split), []);
}

export function useNonSplitTreatmentBoolean<T extends string>(
  treatmentService: TreatmentService,
  split: Split<T>,
  onEnum: string,
): boolean {
  const splitVal = useNonSplitTreatment(treatmentService, split);
  return splitVal === onEnum;
}
